// v2
// Path: frontend/src/components/SyncHub/useSyncStore.js
// Inputs: None (Zustand store hook)
// Outputs: Global state for Sync Hub (accounts, connectionGroups, pipelines, dialogs, token)
// Summary: Centralized state store for the Sync Hub managing data fetching, connection groups, and global confirm/alert dialogs.

import { create } from 'zustand';
import { syncApi } from './syncApiService';

export const useSyncStore = create((set, get) => ({
    // Authentication
    token: null,
    setToken: (token) => set({ token }),

    // Global Data
    accounts: [],
    connectionGroups: [],
    pipelines: [], // Also known as configs
    dbProfiles: [],


    // Dialog State
    dialogs: {
        alert: { isOpen: false, title: '', message: '', onClose: null },
        confirm: { isOpen: false, title: '', message: '', confirmText: 'Confirm', cancelText: 'Cancel', onConfirm: null, onCancel: null }
    },
    showAlert: (message, title = 'Notice') => {
        return new Promise((resolve) => {
            set((state) => ({
                dialogs: {
                    ...state.dialogs,
                    alert: {
                        isOpen: true,
                        title,
                        message,
                        onClose: () => {
                            get().closeAlert();
                            resolve();
                        }
                    }
                }
            }));
        });
    },
    closeAlert: () => set((state) => ({
        dialogs: { ...state.dialogs, alert: { ...state.dialogs.alert, isOpen: false } }
    })),
    showConfirm: (messageOrOptions, defaultTitle = 'Confirm Action') => {
        return new Promise((resolve) => {
            const isObj = typeof messageOrOptions === 'object' && messageOrOptions !== null;
            const message = isObj ? messageOrOptions.message : messageOrOptions;
            const title = isObj ? (messageOrOptions.title || defaultTitle) : defaultTitle;
            const confirmText = isObj ? (messageOrOptions.confirmText || 'Confirm') : 'Confirm';
            const cancelText = isObj ? (messageOrOptions.cancelText || 'Cancel') : 'Cancel';

            set((state) => ({
                dialogs: { 
                    ...state.dialogs, 
                    confirm: { 
                        isOpen: true, 
                        title, 
                        message, 
                        confirmText,
                        cancelText,
                        onConfirm: () => {
                            get().closeConfirm();
                            resolve(true);
                        },
                        onCancel: () => {
                            get().closeConfirm();
                            resolve(false);
                        }
                    } 
                }
            }));
        });
    },
    closeConfirm: () => set((state) => ({
        dialogs: { ...state.dialogs, confirm: { ...state.dialogs.confirm, isOpen: false } }
    })),

    // UI State
    selectedGroup: null,
    setSelectedGroup: (selectedGroup) => set({ selectedGroup }),

    // Fetch Actions
    fetchAccounts: async () => {
        const { token } = get();
        if (!token) return;
        try {
            const data = await syncApi.getAccounts(token);
            set({ accounts: data.accounts || [] });
        } catch (err) {
            console.error('Failed to fetch accounts:', err);
        }
    },
    
    fetchConnectionGroups: async () => {
        const { token } = get();
        if (!token) return;
        try {
            const data = await syncApi.getConnectionGroups(token);
            set({ connectionGroups: data.groups || [] });
        } catch (err) {
            console.error('Failed to fetch connection groups:', err);
        }
    },
    
    fetchPipelines: async () => {
        const { token } = get();
        if (!token) return;
        try {
            const data = await syncApi.getConfigs(token);
            set({ pipelines: data.configs || [] });
        } catch (err) {
            console.error('Failed to fetch pipelines:', err);
        }
    },

    fetchDatabaseProfiles: async () => {
        const { token } = get();
        if (!token) return;
        try {
            const data = await syncApi.getDatabaseProfiles(token);
            set({ dbProfiles: data.profiles || [] });
        } catch (err) {
            console.error('Failed to fetch database profiles:', err);
        }
    },

    // Global Initializer
    fetchAll: async () => {
        await Promise.all([
            get().fetchAccounts(),
            get().fetchConnectionGroups(),
            get().fetchPipelines(),
            get().fetchDatabaseProfiles()
        ]);
    }
}));
